/**
 * Featured products markup builder — the storefront's copy of the designer's
 * `productsMarkup.js`, so the published rail and the canvas preview cannot
 * drift.
 *
 * A rail of product cards, matching SP-main's `prodCard()` and `.sf-prod`. The
 * rail shell — section header, "View all products", arrows, scroller — is the
 * same one Featured brands uses, and the CSS for it is shared rather than
 * restated per section.
 */

import { escapeHtml } from '../../utilities/helper';
import { copyOf, accentTextOf } from './bannerMarkup';

/** Seeds, so a cleared field still reads as a real section. The same strings
 *  the panel shows as placeholders — the two have to agree, because the
 *  placeholder is a promise about what renders when the field is blank. */
export const PRODUCTS_SEED = {
  title: 'Featured products',
  subtitle: "A few of the things we'd point you to first.",
};

export const ARROW_PREV = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>`;
export const ARROW_NEXT = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>`;

/* A box glyph rather than a broken <img>: a product with no photo is ordinary,
   and a torn-image icon reads as a fault in the store. This also replaces the old
   renderer's ShopDot logo fallback, which put our mark on the partner's shelf. */
const PLACEHOLDER = `<div class="ph"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 7h6l2 2h10v10H3z"/></svg></div>`;

/** Prices arrive as numbers, strings or a range, and are shown as given — a card
 *  is not the place to decide a partner's "$20.00 - $249.90" reads wrong. */
const priceLabel = (product) => {
  const raw = product?.price ?? '';
  const text = String(raw).trim();
  if (!text) return '';
  return text.startsWith('$') ? text : `$${text}`;
};

/**
 * Normalise one API product to what a card needs.
 *
 * Field-for-field the designer's `normalizeProduct`: both sides resolve products
 * from the same API, and a card reading `title` on one side and `name` on the
 * other is how a product silently loses its name. Multi-brand products carry
 * their vendor's display name rather than the brand's.
 */
export const normalizeProduct = (product = {}) => ({
  id: product?.id,
  name: String(product?.title || product?.name || '').trim(),
  brand: String(
    product?.is_multi_brand
      ? product?.shopify_vendor?.shopdot_vendor?.display_name || ''
      : product?.brand?.name || product?.brand_name || ''
  ).trim(),
  image: String(
    product?.product_images?.find((i) => i.is_main === '1')?.src ||
    product?.product_images?.[0]?.src ||
    product?.image ||
    ''
  ).trim(),
  price: product?.price_msrp ?? product?.price ?? '',
});

/* `/product/:productId`, singular — the storefront's actual route, and what the
   designer emits. Kept as a named helper because it is the one field on the card
   a canvas can never test. */
const hrefOf = (product) =>
  product?.id ? `/product/${encodeURIComponent(product.id)}` : '#';

/* Exported for Collection spotlight, whose cards must match these exactly. */
export const productCardHtml = (product) => {
  const name = String(product?.name || '').trim();
  const brand = String(product?.brand || '').trim();
  const image = String(product?.image || '').trim();
  const price = priceLabel(product);

  const media = image
    ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(name)}" loading="lazy">`
    : PLACEHOLDER;

  return (
    `<a class="sf-prod" href="${escapeHtml(hrefOf(product))}" aria-label="${escapeHtml(name)}">` +
    `<div class="sf-pimg">${media}</div>` +
    `<div class="sf-pb">` +
    (brand ? `<div class="sf-pbrand">${escapeHtml(brand)}</div>` : '') +
    `<div class="sf-pname">${escapeHtml(name)}</div>` +
    (price ? `<div class="sf-pfoot"><span class="sf-price">${escapeHtml(price)}</span></div>` : '') +
    `</div></a>`
  );
};

/**
 * @param {{ title?: string, subtitle?: string, visible?: boolean }} block
 * @param {Array} products  records already through `normalizeProduct`
 * @param {{ headingFont?: string, themeColor?: string }} options
 * @returns {string} markup, or '' when there is nothing to show
 */
export const buildProductsHtml = (block = {}, products = [], options = {}) => {
  const { headingFont, themeColor } = options;

  const list = Array.isArray(products) ? products.filter(Boolean) : [];
  // Nothing picked yet, so there is no section — not an empty frame, and not the
  // canvas's "Select products to feature" prompt.
  if (!list.length || block.visible === false) return '';

  const heading = copyOf(block.title) || PRODUCTS_SEED.title;
  const sub = copyOf(block.subtitle) || PRODUCTS_SEED.subtitle;
  const headFont = headingFont ? `font-family:${headingFont};` : '';

  return `<section class="sf-sec sf-products-sec">
  <div class="sf-in">
    <div class="sf-sh">
      <div>
        <h2 style="${headFont}">${escapeHtml(heading)}</h2>
        <div class="sub">${escapeHtml(sub)}</div>
      </div>
      <a class="sf-seeall" href="/all-products" style="color:${escapeHtml(themeColor ? accentTextOf(themeColor) : '#2C2F32')}">View all products &rarr;</a>
    </div>
    <div class="sf-railwrap">
      <div class="sf-arrows">
        <button class="sf-arr prev scroll-left" type="button" aria-label="Scroll back">${ARROW_PREV}</button>
        <button class="sf-arr next scroll-right" type="button" aria-label="Scroll forward">${ARROW_NEXT}</button>
      </div>
      <div class="sf-rail cards-scroller">${list.map(productCardHtml).join('')}</div>
    </div>
  </div>
</section>`;
};

export default buildProductsHtml;
